import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Response } from '../models/Response';
import { ConditionDefinition } from '../models/ConditionDefinition';
import { ActionDefinition } from '../models/ActionDefinition';
import { StateTransition, StateTransitionDTO, State } from '../models/StateTransition';

@Injectable({
  providedIn: 'root'
})
export class StateMachineService {

  private baseUrl = `${environment.API_URL}/state-machine`;

  constructor(private http: HttpClient) { }

  // CONDITION DEFINITIONS (Catálogo de condiciones)
  /**
   * Obtener todas las condiciones disponibles
   * @param entityType Filtrar por tipo de entidad (opcional)
   * @param includeInactive Incluir condiciones inactivas (opcional)
   */
  getConditions(entityType?: string, includeInactive: boolean = false): Observable<Response<ConditionDefinition[]>> {
    let params = new HttpParams();
    
    if (entityType) {
      params = params.set('entity_type', entityType);
    }
    
    if (includeInactive) {
      params = params.set('include_inactive', 'true');
    }

    return this.http.get<Response<ConditionDefinition[]>>(`${this.baseUrl}/conditions`, { params });
  }

  /**
   * Obtener una condición específica
   */
  getCondition(id: number): Observable<Response<ConditionDefinition>> {
    return this.http.get<Response<ConditionDefinition>>(`${this.baseUrl}/conditions/${id}`);
  }

  /**
   * Crear nueva condición (solo admin)
   */
  createCondition(data: Partial<ConditionDefinition>): Observable<Response<ConditionDefinition>> {
    return this.http.post<Response<ConditionDefinition>>(`${this.baseUrl}/conditions`, data);
  }

  /**
   * Actualizar condición (solo admin)
   */
  updateCondition(id: number, data: Partial<ConditionDefinition>): Observable<Response<ConditionDefinition>> {
    return this.http.put<Response<ConditionDefinition>>(`${this.baseUrl}/conditions/${id}`, data);
  }

  /**
   * Eliminar condición (solo admin)
   */
  deleteCondition(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${this.baseUrl}/conditions/${id}`);
  }

  /**
   * Activar/desactivar condición
   */
  toggleConditionActive(id: number): Observable<Response<ConditionDefinition>> {
    return this.http.patch<Response<ConditionDefinition>>(`${this.baseUrl}/conditions/${id}/toggle-active`, {});
  }

  // ============================================
  // ACTION DEFINITIONS (Catálogo de acciones)
  // ============================================

  /**
   * Obtener todas las acciones disponibles
   * @param includeInactive Incluir acciones inactivas (opcional)
   */
  getActions(includeInactive: boolean = false): Observable<Response<ActionDefinition[]>> {
    let params = new HttpParams();
    
    if (includeInactive) {
      params = params.set('include_inactive', 'true');
    }

    return this.http.get<Response<ActionDefinition[]>>(`${this.baseUrl}/actions`, { params });
  }

  /**
   * Obtener una acción específica
   */
  getAction(id: number): Observable<Response<ActionDefinition>> {
    return this.http.get<Response<ActionDefinition>>(`${this.baseUrl}/actions/${id}`);
  }

  /**
   * Crear nueva acción (solo admin)
   */
  createAction(data: Partial<ActionDefinition>): Observable<Response<ActionDefinition>> {
    return this.http.post<Response<ActionDefinition>>(`${this.baseUrl}/actions`, data);
  }

  /**
   * Actualizar acción (solo admin)
   */
  updateAction(id: number, data: Partial<ActionDefinition>): Observable<Response<ActionDefinition>> {
    return this.http.put<Response<ActionDefinition>>(`${this.baseUrl}/actions/${id}`, data);
  }

  /**
   * Eliminar acción (solo admin)
   */
  deleteAction(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${this.baseUrl}/actions/${id}`);
  }

  /**
   * Activar/desactivar acción
   */
  toggleActionActive(id: number): Observable<Response<ActionDefinition>> {
    return this.http.patch<Response<ActionDefinition>>(`${this.baseUrl}/actions/${id}/toggle-active`, {});
  }

  // ============================================
  // STATE TRANSITIONS (Transiciones personalizadas)
  // ============================================

  /**
   * Obtener todas las transiciones con filtros opcionales
   */
  getTransitions(filters?: {
    product_id?: number;
    office_id?: number;
    from_state_id?: number;
    to_state_id?: number;
    trigger_event?: string;
    include_inactive?: boolean;
  }): Observable<Response<StateTransition[]>> {
    let params = new HttpParams();

    if (filters) {
      if (filters.product_id) {
        params = params.set('product_id', filters.product_id.toString());
      }
      if (filters.office_id) {
        params = params.set('office_id', filters.office_id.toString());
      }
      if (filters.from_state_id) {
        params = params.set('from_state_id', filters.from_state_id.toString());
      }
      if (filters.to_state_id) {
        params = params.set('to_state_id', filters.to_state_id.toString());
      }
      if (filters.trigger_event) {
        params = params.set('trigger_event', filters.trigger_event);
      }
      if (filters.include_inactive) {
        params = params.set('include_inactive', 'true');
      }
    }

    return this.http.get<Response<StateTransition[]>>(`${this.baseUrl}/transitions`, { params });
  }

  /**
   * Obtener una transición específica
   */
  getTransition(id: number): Observable<Response<StateTransition>> {
    return this.http.get<Response<StateTransition>>(`${this.baseUrl}/transitions/${id}`);
  }

  /**
   * Obtener estados disponibles para crear transiciones
   */
  getAvailableStates(filters?: {
    entity_type?: string;
    product_id?: number;
    office_id?: number;
  }): Observable<Response<State[]>> {
    let params = new HttpParams();

    if (filters) {
      if (filters.entity_type) {
        params = params.set('entity_type', filters.entity_type);
      }
      if (filters.product_id) {
        params = params.set('product_id', filters.product_id.toString());
      }
      if (filters.office_id) {
        params = params.set('office_id', filters.office_id.toString());
      }
    }

    return this.http.get<Response<State[]>>(`${this.baseUrl}/transitions/states/available`, { params });
  }

  /**
   * Crear nueva transición personalizada
   */
  createTransition(data: StateTransitionDTO): Observable<Response<StateTransition>> {
    return this.http.post<Response<StateTransition>>(`${this.baseUrl}/transitions`, data);
  }

  /**
   * Actualizar transición
   */
  updateTransition(id: number, data: StateTransitionDTO): Observable<Response<StateTransition>> {
    return this.http.put<Response<StateTransition>>(`${this.baseUrl}/transitions/${id}`, data);
  }

  /**
   * Eliminar transición
   */
  deleteTransition(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${this.baseUrl}/transitions/${id}`);
  }

  /**
   * Reordenar transiciones en lote (drag & drop)
   */
  reorderTransitions(transitions: Array<{ id: number; order: number }>): Observable<Response<any>> {
    return this.http.post<Response<any>>(`${this.baseUrl}/transitions/reorder`, { transitions });
  }
}